import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Community.css';
import {
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  where,
} from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import TitleSetter from '../components/TitleSetter';
import TopBar from '../components/TopBar';
import { toast } from 'react-toastify';
import {
  addCommunityAssetPack,
  getCommunityAssetPackId,
  listCommunityAssetPacks,
  migrateLegacySelectedAssetPack,
  removeCommunityAssetPack,
} from '../services/communityAssetPacks';
import {
  FiArrowRight,
  FiBarChart2,
  FiBookOpen,
  FiCalendar,
  FiRefreshCw,
  FiSearch,
  FiTarget,
  FiUsers,
  FiX,
} from 'react-icons/fi';

const Community = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [teamFilter, setTeamFilter] = useState('');
  const [teamSearch, setTeamSearch] = useState('');
  const [expandedTeamId, setExpandedTeamId] = useState(null);
  const [teams, setTeams] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [teamLogo, setTeamLogo] = useState(null);
  const [currentTeamId, setCurrentTeamId] = useState(null);
  const [communityAssetPacks, setCommunityAssetPacks] = useState([]);
  const [tutorialStep, setTutorialStep] = useState(1);

  const getAssetPackErrorMessage = (err, fallback) => {
    if (err?.code === 'permission-denied') {
      return 'Firebase rules need to be deployed for Community asset packs.';
    }
    return fallback;
  };

  const tutorialSteps = [
    {
      number: '01',
      title: 'Set Up Season',
      detail: 'Configure the basic identity of your team.',
      lines: [
        'Team name',
        'Team logo',
        'Region',
      ],
      cta: 'Open Database Configuration',
      to: '/settings?tab=database-config&guideStep=1',
    },
    {
      number: '02',
      title: 'Add Players',
      detail: 'Build the active roster your tools will use.',
      lines: [
        'Add each player IGN',
        'Attach player pictures',
        'Set starters and subs',
      ],
      cta: 'Open Player Setup',
      to: '/settings?tab=database-config&guideStep=2',
    },
    {
      number: '03',
      title: 'Add Competitions',
      detail: 'Organize official matches, tournaments, and scrims.',
      lines: [
        'Official match catalog',
        'Practice and scrim labels',
        'Competition regions',
      ],
      cta: 'Open Competition Setup',
      to: '/settings?tab=database-config&guideStep=3',
    },
    {
      number: '04',
      title: 'Add Opponents',
      detail: 'Create the opponent list used by stats and scouting.',
      lines: [
        'Opponent region',
        'Opponent team name',
        'Reusable scouting database',
      ],
      cta: 'Open Opponent Setup',
      to: '/settings?tab=database-config&guideStep=4',
    },
  ];

  const currentTutorial = tutorialSteps[tutorialStep - 1];
  const postSetupGuides = [
    {
      title: 'Strategy',
      to: '/my-strategy',
      description: 'Build and brief team strategies.',
      icon: FiTarget,
    },
    {
      title: 'MyPlaybook',
      to: '/my-playbook',
      description: 'Organize saved plays and setups.',
      icon: FiBookOpen,
    },
    {
      title: 'MyCalendar',
      to: '/my-calendar',
      description: 'Plan practices, scrims, and match days.',
      icon: FiCalendar,
    },
    {
      title: 'MyStats',
      to: '/my-stats',
      description: 'Review team and player performance.',
      icon: FiBarChart2,
    },
  ];

  const loadTeams = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, 'mediaLibrary'), where('type', '==', 'team'))
      );
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setTeams(items);
    } catch (err) {
      console.error('Failed to load teams', err);
    } finally {
      setLoading(false);
    }
  };

  const loadProfile = async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      const snap = await getDoc(doc(db, `users/${user.uid}`));
      if (snap.exists()) {
        const data = snap.data();
        setUserProfile(data);
        setTeamLogo(data.teamLogo || null);
        if (data.teamId) setCurrentTeamId(data.teamId);
      }
    } catch (err) {
      console.error('Failed to load profile', err);
    }
  };

  const loadCommunityAssetPacks = async (teamId) => {
    if (!teamId) {
      setCommunityAssetPacks([]);
      return;
    }
    try {
      const packs = await listCommunityAssetPacks(teamId);
      if (packs.length > 0) {
        setCommunityAssetPacks(packs);
        return;
      }

      const migration = await migrateLegacySelectedAssetPack(
        teamId,
        auth.currentUser?.uid || null
      );
      setCommunityAssetPacks(migration.pack ? [migration.pack] : []);
    } catch (err) {
      console.error('Failed to load community asset packs', err);
      setCommunityAssetPacks([]);
    }
  };

  useEffect(() => {
    loadTeams();
    loadProfile();
  }, []);

  useEffect(() => {
    if (currentTeamId) {
      loadCommunityAssetPacks(currentTeamId);
    }
  }, [currentTeamId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setExpandedTeamId(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const extractPlayers = (team) => {
    if (Array.isArray(team.players) && team.players.length > 0) return team.players;
    if (Array.isArray(team.roster) && team.roster.length > 0) return team.roster;
    if (Array.isArray(team.playerPics) && team.playerPics.length > 0) {
      return team.playerPics.map((pic, idx) => ({
        name: (team.teamName || team.name || 'Player') + ` #${idx + 1}`,
        avatar: pic,
      }));
    }
    return [];
  };

  const getTeamLogoUrl = (team) => team.logo || team.url || team.teamLogo || team.image || '';
  const getTeamName = (team) => team.teamName || team.name || 'Unnamed Team';
  const getTeamSecondary = (team) => {
    const secondary = team.region || team.shortName || '';
    return secondary && secondary !== getTeamName(team) ? secondary : '';
  };
  const getTeamPreviewMeta = (team) => {
    const playersLabel = `${extractPlayers(team).length} Players`;
    const secondary = getTeamSecondary(team);
    return secondary ? `${secondary} - ${playersLabel}` : playersLabel;
  };
  const getImportedPack = (team) => {
    const packId = getCommunityAssetPackId(team);
    return communityAssetPacks.find((pack) =>
      pack.id === packId ||
      pack.sourceLibraryId === team.id ||
      (team.teamId && pack.sourceTeam === team.teamId)
    );
  };

  const isTeamImported = (team) => !!getImportedPack(team);

  const filteredTeams = useMemo(() => {
    const queryText = teamSearch.trim().toLowerCase();
    return teams.filter((t) => {
      const filterId = teamFilter?.trim();
      if (filterId && (t.id !== filterId && (t.teamId || '') !== filterId)) return false;
      if (!queryText) return true;

      const searchable = [
        t.name,
        t.teamName,
        t.shortName,
        t.teamId,
        t.id,
        t.region,
      ].filter(Boolean).join(' ').toLowerCase();

      return searchable.includes(queryText);
    });
  }, [teams, teamFilter, teamSearch]);

  const expandedTeam = filteredTeams.find((t) => t.id === expandedTeamId);

  const addTeamToStrategyRoom = async (team) => {
    if (!currentTeamId) {
      toast.error('Join or create a team first.');
      return;
    }
    try {
      const savedPack = await addCommunityAssetPack(
        currentTeamId,
        team,
        auth.currentUser?.uid || null
      );
      setCommunityAssetPacks((prev) => [
        savedPack,
        ...prev.filter((pack) => pack.id !== savedPack.id),
      ]);
      toast.success('Team added to Strategy Room.');
    } catch (err) {
      console.error('Failed to add team asset pack', err);
      toast.error(getAssetPackErrorMessage(err, 'Failed to add team to Strategy Room.'));
    }
  };

  const removeTeamFromStrategyRoom = async (team) => {
    if (!currentTeamId) return;
    const importedPack = getImportedPack(team);
    const packId = importedPack?.id || getCommunityAssetPackId(team);
    try {
      await removeCommunityAssetPack(currentTeamId, packId);
      setCommunityAssetPacks((prev) => prev.filter((pack) => pack.id !== packId));
      toast.success('Team removed from Strategy Room.');
    } catch (err) {
      console.error('Failed to remove team asset pack', err);
      toast.error(getAssetPackErrorMessage(err, 'Failed to remove team from Strategy Room.'));
    }
  };

  const renderAssetAction = (team, className = 'community-btn community-asset-btn') => {
    const imported = isTeamImported(team);
    return (
      <button
        className={`${className}${imported ? ' active' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          imported ? removeTeamFromStrategyRoom(team) : addTeamToStrategyRoom(team);
        }}
      >
        {imported ? 'Remove from Strategy Room' : 'Add to Strategy Room'}
        {!imported && <FiArrowRight aria-hidden="true" />}
      </button>
    );
  };

  return (
    <>
      <TitleSetter title="Community Library" />
      <TopBar
        userProfile={userProfile}
        setUserProfile={setUserProfile}
        teamLogo={teamLogo}
        setTeamLogo={setTeamLogo}
      />
      <main className="community-container">
        <header className="community-page-head">
          <p className="community-eyebrow">Community</p>
          <h1>Community Hub</h1>
          <p className="community-subtitle">
            Set up your workspace, discover TheBox, and use shared team assets in Strategy Room.
          </p>
        </header>

        <section className="community-section community-quick-start" aria-label="Quick start">
          <div className="community-section-header compact">
            <div>
              <p className="community-eyebrow">Quick Start</p>
              <h2 className="community-section-title">Finish setting up your workspace.</h2>
            </div>
            <span className="community-step-count">Step {tutorialStep} of {tutorialSteps.length}</span>
          </div>

          <div className="community-quick-grid">
            <div className="community-step-rail" aria-label="Setup steps">
              {tutorialSteps.map((step, idx) => (
                <button
                  key={step.title}
                  className={`community-step-jump-item ${tutorialStep === idx + 1 ? 'active' : ''}`}
                  onClick={() => setTutorialStep(idx + 1)}
                >
                  <span>{step.number}</span>
                  <strong>{step.title}</strong>
                </button>
              ))}
            </div>

            <article className="community-tutorial">
              <p className="community-tutorial-kicker">{currentTutorial.title}</p>
              <h3 className="community-tutorial-title">{currentTutorial.detail}</h3>

              <ul className="community-tutorial-step">
                {currentTutorial.lines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>

              <div className="community-tutorial-controls">
                <button
                  className="community-btn ghost"
                  onClick={() => setTutorialStep((prev) => Math.max(1, prev - 1))}
                  disabled={tutorialStep === 1}
                >
                  Previous
                </button>
                <button
                  className="community-btn primary"
                  onClick={() => navigate(currentTutorial.to)}
                >
                  {currentTutorial.cta}
                  <FiArrowRight aria-hidden="true" />
                </button>
                <button
                  className="community-btn ghost"
                  onClick={() => setTutorialStep((prev) => Math.min(tutorialSteps.length, prev + 1))}
                  disabled={tutorialStep === tutorialSteps.length}
                >
                  Next
                </button>
              </div>
            </article>
          </div>
        </section>

        <section className="community-section community-library-section" aria-label="Team library">
          <div className="community-header">
            <div>
              <p className="community-eyebrow">Team Library</p>
              <h2 className="community-section-title">Use shared assets in Strategy Room.</h2>
              <p className="community-section-subtitle">
                Add team asset packs to make their logos and players available in Strategy Room.
              </p>
              <p className="community-library-status">
                {communityAssetPacks.length} {communityAssetPacks.length === 1 ? 'team' : 'teams'} added to Strategy Room
              </p>
            </div>
            <div className="community-actions">
              <label className="community-search">
                <FiSearch aria-hidden="true" />
                <input
                  value={teamSearch}
                  onChange={(e) => setTeamSearch(e.target.value)}
                  placeholder="Search teams"
                />
              </label>
              <select
                className="community-select"
                value={teamFilter}
                onChange={(e) => setTeamFilter(e.target.value)}
              >
                <option value="">All teams</option>
                {teams.map((t) => {
                  const optionId = t.teamId || t.id;
                  return (
                    <option key={t.id} value={optionId}>
                      {t.teamName || t.name || optionId}
                    </option>
                  );
                })}
              </select>
              <button className="community-btn ghost" onClick={loadTeams} disabled={loading}>
                <FiRefreshCw aria-hidden="true" />
                {loading ? 'Loading...' : 'Reload'}
              </button>
            </div>
          </div>

          <div className="community-grid">
            {loading && teams.length === 0 ? (
              Array.from({ length: 6 }).map((_, idx) => (
                <div className="community-card community-card-skeleton" key={idx} aria-hidden="true" />
              ))
            ) : (
              filteredTeams.map((team) => {
                const teamLogoUrl = getTeamLogoUrl(team);
                const players = extractPlayers(team);
                const imported = isTeamImported(team);
                return (
                  <article
                    className={`community-card ${imported ? 'added' : ''}`}
                    key={team.id}
                    onClick={() => setExpandedTeamId(team.id)}
                  >
                    {imported && <span className="community-active-badge">Added to Strategy Room</span>}
                    <div className="community-logo-block">
                      {teamLogoUrl ? (
                        <img src={teamLogoUrl} alt={getTeamName(team)} className="community-thumb" />
                      ) : (
                        <FiUsers aria-hidden="true" />
                      )}
                    </div>
                    <div className="community-meta">
                      <p className="community-type">{getTeamName(team)}</p>
                      {getTeamSecondary(team) && (
                        <p className="community-team">{getTeamSecondary(team)}</p>
                      )}
                    </div>
                    <div className="community-card-stat">
                      <FiUsers aria-hidden="true" />
                      <span>{players.length} players</span>
                    </div>
                    <div className="community-card-actions">
                      <button
                        className="community-roster-link"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedTeamId(team.id);
                        }}
                      >
                        View Roster
                        <FiArrowRight aria-hidden="true" />
                      </button>
                      {renderAssetAction(team)}
                    </div>
                  </article>
                );
              })
            )}

            {!loading && filteredTeams.length === 0 && (
              <div className="community-empty">
                <h3>No teams found</h3>
                <p>Try changing your filter or reload the Team Library.</p>
                <button className="community-btn ghost" onClick={loadTeams}>
                  <FiRefreshCw aria-hidden="true" />
                  Reload
                </button>
              </div>
            )}
          </div>
        </section>

        <section className="community-section community-next-guide" aria-label="Explore TheBox">
          <div className="community-section-header">
            <p className="community-eyebrow">Explore TheBox</p>
            <h2 className="community-section-title">Jump into the tools available to your team.</h2>
          </div>
          <div className="community-next-grid">
            {postSetupGuides.map((guide) => {
              const Icon = guide.icon;
              return (
                <button
                  className="community-next-card"
                  key={guide.title}
                  onClick={() => navigate(guide.to)}
                >
                  <Icon aria-hidden="true" />
                  <span>
                    <strong>{guide.title}</strong>
                    <small>{guide.description}</small>
                  </span>
                  <FiArrowRight aria-hidden="true" />
                </button>
              );
            })}
          </div>
        </section>

        {expandedTeam && (
          <div className="community-overlay" onClick={() => setExpandedTeamId(null)}>
            <div className="community-overlay-panel" onClick={(e) => e.stopPropagation()}>
              <div className="community-preview-head">
                <div className="community-preview-identity">
                  <div className="community-logo-block">
                    {getTeamLogoUrl(expandedTeam) ? (
                      <img
                        src={getTeamLogoUrl(expandedTeam)}
                        alt={getTeamName(expandedTeam)}
                        className="community-thumb"
                      />
                    ) : (
                      <FiUsers aria-hidden="true" />
                    )}
                  </div>
                <div className="community-meta">
                  <p className="community-eyebrow">Team Preview</p>
                  <h2>{getTeamName(expandedTeam)}</h2>
                  <p className="community-team">
                    {getTeamPreviewMeta(expandedTeam)}
                  </p>
                  {isTeamImported(expandedTeam) && (
                    <span className="community-active-badge">Added to Strategy Room</span>
                  )}
                </div>
                </div>
                <button
                  className="community-icon-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedTeamId(null);
                  }}
                  aria-label="Close roster"
                >
                  <FiX aria-hidden="true" />
                </button>
              </div>

              <div className="community-preview-body">
                <div>
                  <p className="community-eyebrow">Roster</p>
                  <p className="community-preview-copy">
                    These assets will become available in your Strategy Room.
                  </p>
                </div>
                {extractPlayers(expandedTeam).length > 0 ? (
                  <div className="community-roster">
                    {extractPlayers(expandedTeam).map((p, idx) => {
                      const avatar = p.avatar || p.pic || p.url || p.image || '';
                      return (
                        <div className="community-player-row" key={`${p.name || 'player'}-${idx}`}>
                          {avatar ? (
                            <img src={avatar} alt={p.name || `Player ${idx + 1}`} className="community-player-thumb" />
                          ) : (
                            <span className="community-player-placeholder">{idx + 1}</span>
                          )}
                          <span>{p.name || `Player ${idx + 1}`}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="community-empty compact">
                    <h3>No roster assets</h3>
                    <p>This team pack does not include player images yet.</p>
                  </div>
                )}
              </div>

              <div className="community-preview-actions">
                <button
                  className="community-btn ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedTeamId(null);
                  }}
                >
                  Close
                </button>
                {renderAssetAction(expandedTeam)}
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
};

export default Community;
